import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ChevronLeft, ChevronRight, Maximize2, Loader2, Image as ImageIcon } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import ParticleField from "@/components/site/ParticleField";
import { SectionTag, Reveal } from "@/components/site/Reveal";
import { fetchGallery, toAbsolute } from "@/lib/api";

const L = {
  ar: {
    tag: "معرض أعمالنا",
    title: "لمحات من ميدان العمل",
    subtitle: "مقتطفات حقيقية من مشاريعنا وفريقنا وفعالياتنا — لأن الصورة تحكي أكثر من ألف كلمة.",
    empty: "قريبًا سنشارك لقطات من أعمالنا الميدانية.",
    categories: {
      all: "الكل",
      field: "من الميدان",
      team: "الفريق",
      event: "فعاليات",
      implementation: "تنفيذ مشاريع",
      office: "مقرّاتنا",
      other: "أخرى",
    },
  },
  en: {
    tag: "Our Works",
    title: "Glimpses from the field",
    subtitle: "Real snapshots from our projects, teams, and events — because a picture tells more than a thousand words.",
    empty: "Field snapshots from our work coming soon.",
    categories: {
      all: "All", field: "In the Field", team: "The Team",
      event: "Events", implementation: "Implementation", office: "Offices", other: "Other",
    },
  },
  ku: {
    tag: "کاری ئێمە",
    title: "دیمەنێک لە مەیدانی کار",
    subtitle: "دیمەنی ڕاستەقینە لە پرۆژە، تیم و بۆنەکانمان.",
    empty: "بەم زووانە دیمەنی کاری میدانیمان بڵاو دەکەینەوە.",
    categories: {
      all: "هەموو", field: "لە مەیدان", team: "تیم",
      event: "بۆنە", implementation: "جێبەجێکردن", office: "ئۆفیس", other: "هیتر",
    },
  },
};

const asEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
};

// Deterministic "random" span for masonry variety — bigger cards for videos and every 5th image
const cardSpan = (item, idx) => {
  if (item.media_type === "video") return "sm:col-span-2 sm:row-span-2";
  if (idx % 7 === 3) return "sm:row-span-2";
  if (idx % 11 === 5) return "sm:col-span-2";
  return "";
};

function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);

  const embedSrc = item.media_type === "video" ? asEmbed(item.media_url) : null;
  const absSrc = toAbsolute(item.media_url);

  return (
    <motion.div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/95 backdrop-blur-md p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 end-4 h-11 w-11 grid place-items-center rounded-full bg-black/70 border border-white/20 hover:border-[color:var(--primary)] text-white z-10" data-testid="lightbox-close">
        <X size={20} />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute start-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-black/70 border border-white/20 hover:border-[color:var(--primary)] text-white z-10" data-testid="lightbox-prev">
            <ChevronLeft size={22} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute end-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-black/70 border border-white/20 hover:border-[color:var(--primary)] text-white z-10" data-testid="lightbox-next">
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <motion.div
        key={item.id}
        className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full max-h-[75vh] rounded-2xl overflow-hidden bg-black flex items-center justify-center">
          {item.media_type === "video" ? (
            embedSrc ? (
              <div className="w-full aspect-video">
                <iframe src={embedSrc} className="w-full h-full" title={item.title || "video"} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <video src={absSrc} className="max-h-[75vh] max-w-full" controls autoPlay playsInline data-testid="lightbox-video" />
            )
          ) : (
            <img src={absSrc} alt={item.title} className="max-h-[75vh] max-w-full object-contain" data-testid="lightbox-image" />
          )}
        </div>
        {(item.title || item.description) && (
          <div className="mt-3 text-center text-white max-w-3xl">
            {item.title && <div className="text-lg font-extrabold">{item.title}</div>}
            {item.description && <div className="text-sm text-slate-400 mt-1">{item.description}</div>}
          </div>
        )}
        <div className="mt-2 text-xs text-slate-500">{index + 1} / {items.length}</div>
      </motion.div>
    </motion.div>
  );
}

export default function Gallery() {
  const { lang } = useLang();
  const T = L[lang] || L.ar;
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => { fetchGallery(lang).then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [lang]);

  const list = items || [];
  const cats = useMemo(() => Array.from(new Set(list.map((r) => r.category).filter(Boolean))), [list]);
  const filtered = useMemo(() => filter === "all" ? list : list.filter((r) => r.category === filter), [list, filter]);

  const openAt = useCallback((i) => setLightboxIdx(i), []);
  const close = useCallback(() => setLightboxIdx(-1), []);
  const prev = useCallback(() => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length), [filtered.length]);
  const next = useCallback(() => setLightboxIdx((i) => (i + 1) % filtered.length), [filtered.length]);

  return (
    <SiteLayout>
      <Seo title={T.title} description={T.subtitle} />

      <section className="relative overflow-hidden pt-16 pb-6" data-testid="gallery-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <SectionTag>{T.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{T.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{T.subtitle}</p>
        </div>
      </section>

      <section className="section-pad pt-4">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
              {["all", ...cats].map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={"rounded-full px-4 py-1.5 text-xs font-bold border transition-colors " + (filter === c ? "text-[#04120a] border-transparent" : "border-white/10 text-slate-300 hover:border-[color:var(--primary)]")}
                  style={filter === c ? { background: "var(--primary)" } : {}}
                  data-testid={`gallery-filter-${c}`}
                >
                  {T.categories[c] || c}
                </button>
              ))}
            </div>
          )}

          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500 flex flex-col items-center gap-3">
              <ImageIcon size={48} className="text-slate-700" />
              {T.empty}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[200px]" data-testid="gallery-grid">
              {filtered.map((it, i) => {
                const poster = it.poster || (it.media_type === "image" ? it.media_url : "");
                return (
                  <Reveal key={it.id} delay={0.03 + Math.min(i, 12) * 0.03}>
                    <button
                      onClick={() => openAt(i)}
                      className={"group relative w-full h-full overflow-hidden rounded-2xl border border-white/10 hover:border-[color:var(--primary)]/60 transition-all duration-500 " + cardSpan(it, i)}
                      style={{ boxShadow: "0 8px 30px -12px rgba(0,242,96,0.1)" }}
                      data-testid={`gallery-item-${i}`}
                    >
                      {poster ? (
                        <img src={toAbsolute(poster)} alt={it.title || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full grid place-items-center" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,242,96,0.18), rgba(0,0,0,0.6))" }}>
                          <ImageIcon size={40} className="text-primary/40" />
                        </div>
                      )}
                      {/* dark gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />

                      {it.media_type === "video" && (
                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                          <div
                            className="h-14 w-14 rounded-full grid place-items-center bg-[color:var(--primary)] text-[#04120a] group-hover:scale-110 transition-transform duration-500"
                            style={{ boxShadow: "0 0 30px rgba(0,242,96,0.5)" }}
                          >
                            <Play size={22} className="ms-0.5" fill="currentColor" />
                          </div>
                        </div>
                      )}

                      {it.title && (
                        <div className="absolute bottom-0 inset-x-0 p-3 text-start">
                          <div className="text-sm font-extrabold text-white line-clamp-1">{it.title}</div>
                          {it.category && (
                            <div className="text-[10px] font-bold text-primary uppercase tracking-wider mt-0.5">
                              {T.categories[it.category] || it.category}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="absolute top-3 end-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="h-8 w-8 rounded-full grid place-items-center bg-black/60 border border-white/20 text-white">
                          <Maximize2 size={13} />
                        </div>
                      </div>
                    </button>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightboxIdx >= 0 && (
          <Lightbox items={filtered} index={lightboxIdx} onClose={close} onPrev={prev} onNext={next} />
        )}
      </AnimatePresence>
    </SiteLayout>
  );
}
